import { create } from "zustand";
import produce from "immer";
import * as api from "../services/delivery_notes";
import { arrayToByIdsAllIds } from "./utils";
import i18n from "../translations/i18n";
import { showApiError } from "../utils/apiError";

export const DELIVERY_NOTE_STATE = Object.freeze({
  PENDING: "pending",
  DRAFTED: "drafted",
  DELIVERED: "delivered",
  REVIEWED: "reviewed",
  BILLED: "billed",
  CANCELED: "canceled",
});

export const DELIVERY_NOTE_ATTRS = Object.freeze({
  INVOICE_NUMBER: "invoice.number",
});

// Backend list filter that hides canceled notes (used by linking pickers).
const NOT_CANCELED_FILTER = "not_canceled";

export const useDeliveryNotesStore = create((set) => ({
  byIds: {},
  allIds: [],
  total: 0,
  totalDue: 0,
  totalReviewable: 0,
  totalBillable: 0,
  totalDraft: 0,
  lastFetchParams: null,
  fetchSome: async ({
    q = "",
    filter = "",
    limit = 20,
    offset = 0,
    sort_by = "number",
    sort_dir = "desc",
  }) => {
    const params = { q, filter, limit, offset, sort_by, sort_dir };
    const some = await api.getSome(params);
    set(
      produce((draft) => {
        [draft.byIds, draft.allIds] = arrayToByIdsAllIds(some.data);
        draft.total = some.total;
        draft.lastFetchParams = params;

        if (filter === "review") draft.totalReviewable = some.total;
        if (filter === "bill") draft.totalBillable = some.total;
        if (filter === "due") draft.totalDue = some.total;

        // TODO make pagination work if we keep all data
        //[draft.byIds, draft.allIds] = arrayToByIdsAllIds([...Object.values(draft.byIds), ...some.data]);
        //draft.total = some.total;
      }),
    );
  },
  fetchDueTotal: async () => {
    const some = await api.getSome({ filter: "due" });
    set(
      produce((draft) => {
        draft.totalDue = some.total;
      }),
    );
  },
  fetchBillableTotal: async () => {
    const some = await api.getSome({ filter: "bill" });
    set(
      produce((draft) => {
        draft.totalBillable = some.total;
      }),
    );
  },
  fetchReviewableTotal: async () => {
    const some = await api.getSome({ filter: "review" });
    set(
      produce((draft) => {
        draft.totalReviewable = some.total;
      }),
    );
  },
  fetchDraftTotal: async () => {
    const some = await api.getSome({ filter: "draft" });
    set(
      produce((draft) => {
        draft.totalDraft = some.total;
      }),
    );
  },
  generatePdf: async ({ id }) => {
    return await api.generatePdf({ id });
  },
  assignDeliveryNote: async ({ id, assigned_to_id }) => {
    const one = await api.assignDeliveryNote({ id, assigned_to_id });
    if (!!one.data) {
      set(
        produce((draft) => {
          draft.byIds[one.data.id] = one.data;
        }),
      );
    }
    if (!!one.error) {
      showApiError(one.error, i18n.t.bind(i18n));
    }
    return one;
  },
  changeState: async ({ id, state, attrs = {} }) => {
    const one = await api.changeState({ id, state, attrs });
    if (!!one.data) {
      set(
        produce((draft) => {
          draft.byIds[one.data.id] = one.data;
        }),
      );
    }
    if (!!one.error) {
      showApiError(one.error, i18n.t.bind(i18n));
    }
    return one;
  },
  // Read-only search for pickers (e.g. linking a note to a work order):
  // returns results without touching byIds/allIds so list pages keep state.
  searchSome: async ({ q = "", limit = 10, offset = 0 }) => {
    return api.getSome({ q, limit, offset, filter: NOT_CANCELED_FILTER });
  },
  createOne: async ({
    state,
    template,
    customer,
    customer_property,
    items,
    uploads,
    comments,
    documentation_schema,
    documentation,
    remark,
    customer_signature,
    customer_signature_name,
    customer_signature_date,
    employee_signature,
    employee_signature_name,
    coworker_ids,
    performed_by_id,
    work_order_id,
  }) => {
    const one = await api.createOne({
      state,
      template,
      customer,
      customer_property,
      items,
      uploads,
      comments,
      documentation_schema,
      documentation,
      remark,
      customer_signature,
      customer_signature_name,
      customer_signature_date,
      employee_signature,
      employee_signature_name,
      coworker_ids,
      performed_by_id,
      work_order_id,
    });
    if (!!one.data) {
      set(
        produce((draft) => {
          draft.byIds[one.data.id] = one.data;
          if (!draft.allIds.includes(one.data.id)) {
            draft.allIds.unshift(one.data.id);
          }
        }),
      );
    }
    return one;
  },
  updateOne: async ({
    id,
    version,
    state,
    template,
    customer,
    customer_property,
    items,
    uploads,
    comments,
    documentation_schema,
    documentation,
    remark,
    customer_signature,
    customer_signature_name,
    customer_signature_date,
    employee_signature,
    employee_signature_name,
    coworker_ids,
    performed_by_id,
  }) => {
    const one = await api.updateOne({
      id,
      state,
      version,
      template,
      customer,
      customer_property,
      items,
      uploads,
      comments,
      documentation_schema,
      documentation,
      remark,
      customer_signature,
      customer_signature_name,
      customer_signature_date,
      employee_signature,
      employee_signature_name,
      coworker_ids,
      performed_by_id,
    });
    if (!!one.data) {
      set(
        produce((draft) => {
          draft.byIds[one.data.id] = one.data;
        }),
      );
    }
    return one;
  },
  getById: async ({ id }) => {
    const one = await api.getById({ id });
    set(
      produce((draft) => {
        draft.byIds[one.data.id] = one.data;
        if (!draft.allIds.includes(one.data.id)) {
          draft.allIds.push(one.data.id);
        }
      }),
    );
  },

  addComment: async ({ id, text }) => {
    const result = await api.addComment({ id, text });
    if (result.data) {
      set(
        produce((draft) => {
          if (draft.byIds[id]) {
            draft.byIds[id].comments.push(result.data);
          }
        }),
      );
    }
    if (result.error) {
      showApiError(result.error, i18n.t.bind(i18n));
    }
    return result;
  },
  updateComment: async ({ id, commentId, text }) => {
    const result = await api.updateComment({ id, commentId, text });
    if (result.data) {
      set(
        produce((draft) => {
          if (draft.byIds[id]) {
            const idx = draft.byIds[id].comments.findIndex(
              (c) => c.id === commentId,
            );
            if (idx !== -1) {
              draft.byIds[id].comments[idx] = result.data;
            }
          }
        }),
      );
    }
    if (result.error) {
      showApiError(result.error, i18n.t.bind(i18n));
    }
    return result;
  },
  deleteComment: async ({ id, commentId }) => {
    const result = await api.deleteComment({ id, commentId });
    if (!result.error) {
      set(
        produce((draft) => {
          if (draft.byIds[id]) {
            draft.byIds[id].comments = draft.byIds[id].comments.filter(
              (c) => c.id !== commentId,
            );
          }
        }),
      );
    }
    if (result.error) {
      showApiError(result.error, i18n.t.bind(i18n));
    }
    return result;
  },

  deleteFile: async ({ id, fileId }) => {
    const result = await api.deleteFile({ id, fileId });
    if (!result.error) {
      set(
        produce((draft) => {
          if (draft.byIds[id]) {
            draft.byIds[id].files = draft.byIds[id].files.filter(
              (f) => f.id !== fileId,
            );
          }
        }),
      );
    }
    if (result.error) {
      showApiError(result.error, i18n.t.bind(i18n));
    }
    return result;
  },

  /* Check if a note is in a given state. Its complex because the state transitions are stored in a list and the last one is the current one.*/
  removeFromList: (id) => {
    set(
      produce((draft) => {
        draft.allIds = draft.allIds.filter((i) => i !== id);
        delete draft.byIds[id];
        draft.total = Math.max(0, draft.total - 1);
      }),
    );
  },

  isInState: (note, state) => {
    return (
      note?.state_transitions[note?.state_transitions.length - 1].state ===
      state
    );
  },

  isOwner: (note, user) => {
    return note?.created_by.id === user.id;
  },
}));
